import { GameState } from '../../core/GameState.js';
import { CARGO_TYPES, canShipCarryCargo } from '../../config/cargoTypes.js';
import { calculateRoute } from '../../utils/seaRoutes.js';

/**
 * Helper module for Market Analyst calculations.
 * Extracts complex trade analysis logic from MarketPanel.
 */

/**
 * Calculate profit for a trade: buy cargo at refPort, sell at destPort.
 * Returns { profit, profitPercent, profitPerNm, fuelCost, buyPrice, sellPrice, distance }
 */
export function calculateTradeProfit(cargoId, quantity, ship, refPort, destPort) {
  const refPrices = GameState.marketPrices.get(refPort.id) || {};
  const destPrices = GameState.marketPrices.get(destPort.id) || {};

  const buyPrice = refPrices[cargoId] || 0;

  // Sell price: 0.5x if producing, 0.9x otherwise
  const rawDestPrice = destPrices[cargoId] || 0;
  const isProduced = destPort.producedGoods.includes(cargoId);
  const sellMod = isProduced ? 0.5 : 0.9;
  const sellPrice = Math.round(rawDestPrice * sellMod);

  // Calculate distance and fuel cost
  const route = calculateRoute(refPort.position, destPort.position);
  const distance = route.distance;
  const fuelNeeded = ship.estimateFuelForVoyage(distance);
  const fuelCost = Math.round(fuelNeeded * refPort.fuelPrice);

  // Net profit
  const revenue = sellPrice * quantity;
  const cost = buyPrice * quantity;
  const profit = revenue - cost - fuelCost;
  const profitPercent = buyPrice > 0 ? ((sellPrice - buyPrice) / buyPrice) : 0;
  const profitPerNm = distance > 0 ? profit / distance : 0;

  return {
    profit,
    profitPercent,
    profitPerNm,
    fuelCost,
    buyPrice,
    sellPrice,
    distance,
    revenue,
    cost
  };
}

/**
 * Find the best trade opportunity for a given ship at its current port.
 * Returns { cargoId, cargoName, destPort, quantity, profit, profitPercent, distance }
 */
export function findBestTrade(ship) {
  if (!ship.currentPortId) return null;

  const refPort = GameState.getPort(ship.currentPortId);
  if (!refPort) return null;

  const availableCapacity = ship.getAvailableCapacity();
  if (availableCapacity <= 0) return null;

  const portStock = GameState.portStock.get(refPort.id) || {};
  const allPorts = GameState.getAllPorts();

  let bestTrade = null;
  let bestProfit = -Infinity;

  // Check all compatible cargo types
  for (const cargo of Object.values(CARGO_TYPES)) {
    if (!canShipCarryCargo(ship.type, cargo.id)) continue;

    const stockAvailable = portStock[cargo.id] || 0;
    if (stockAvailable <= 0) continue;

    // Consider quantity: up to available capacity or stock, whichever is less
    const quantity = Math.min(availableCapacity, stockAvailable);

    // Check all destination ports (excluding current port)
    for (const destPort of allPorts) {
      if (destPort.id === refPort.id) continue;

      // Skip ports the ship can't dock at
      const shipCapacity = ship.getCargoCapacity();
      const portLimit = { tiny: 100, small: 500, medium: 2000, large: 8000, major: 99999 }[destPort.size];
      if (shipCapacity > portLimit) continue;

      // Calculate profit
      const trade = calculateTradeProfit(cargo.id, quantity, ship, refPort, destPort);

      // Only consider profitable trades
      if (trade.profit > bestProfit && trade.profit > 0) {
        bestProfit = trade.profit;
        bestTrade = {
          cargoId: cargo.id,
          cargoName: cargo.name,
          destPort,
          quantity,
          ...trade
        };
      }
    }
  }

  return bestTrade;
}

/**
 * Find the best trade opportunity across all player ships.
 * Returns { ship, cargoId, cargoName, destPort, quantity, profit, profitPercent, distance }
 */
export function findBestTradeFleetwide() {
  const ships = GameState.getAllShips().filter(s => s.currentPortId && s.status === 'docked');

  let bestTrade = null;
  let bestProfit = -Infinity;

  for (const ship of ships) {
    const trade = findBestTrade(ship);
    if (trade && trade.profit > bestProfit) {
      bestProfit = trade.profit;
      bestTrade = { ship, ...trade };
    }
  }

  return bestTrade;
}

/**
 * Analyze all cargo at current port and rank by profit potential.
 * Returns array of { cargoId, cargoName, bestDestPort, profit, profitPercent, profitPerNm }
 */
export function rankTradesByProfit(ship, refPort) {
  if (!refPort) return [];

  const portStock = GameState.portStock.get(refPort.id) || {};
  const allPorts = GameState.getAllPorts();
  const availableCapacity = ship.getAvailableCapacity();

  const trades = [];

  // Check all compatible cargo types
  for (const cargo of Object.values(CARGO_TYPES)) {
    if (!canShipCarryCargo(ship.type, cargo.id)) continue;

    const stockAvailable = portStock[cargo.id] || 0;
    if (stockAvailable <= 0) continue;

    const quantity = Math.min(availableCapacity, stockAvailable);

    // Find best destination for this cargo
    let bestDestProfit = -Infinity;
    let bestDestPort = null;
    let bestTrade = null;

    for (const destPort of allPorts) {
      if (destPort.id === refPort.id) continue;

      // Skip ports the ship can't dock at
      const shipCapacity = ship.getCargoCapacity();
      const portLimit = { tiny: 100, small: 500, medium: 2000, large: 8000, major: 99999 }[destPort.size];
      if (shipCapacity > portLimit) continue;

      const trade = calculateTradeProfit(cargo.id, quantity, ship, refPort, destPort);

      if (trade.profit > bestDestProfit) {
        bestDestProfit = trade.profit;
        bestDestPort = destPort;
        bestTrade = trade;
      }
    }

    if (bestTrade && bestDestPort) {
      trades.push({
        cargoId: cargo.id,
        cargoName: cargo.name,
        bestDestPort,
        quantity,
        ...bestTrade
      });
    }
  }

  // Sort by profit descending
  trades.sort((a, b) => b.profit - a.profit);

  return trades;
}

/**
 * Get price trend for a specific port-cargo pair.
 * Returns 'rising' | 'falling' | 'stable'
 *
 * Note: This accesses GameState.priceHistory directly.
 * The trend calculation compares recent prices (last 4) vs older prices (entries 4-8).
 */
export function getPriceTrend(portId, cargoId) {
  const key = `${portId}_${cargoId}`;
  const history = GameState.priceHistory.get(key) || [];

  // Need at least 8 data points for meaningful trend analysis
  if (history.length < 8) return 'stable';

  // Calculate average of recent prices (last 4 entries)
  const recentSlice = history.slice(-4);
  const recentAvg = recentSlice.reduce((sum, entry) => sum + entry.price, 0) / recentSlice.length;

  // Calculate average of older prices (entries 4-8 from the end)
  const olderSlice = history.slice(-8, -4);
  const olderAvg = olderSlice.reduce((sum, entry) => sum + entry.price, 0) / olderSlice.length;

  // Calculate percentage change
  const percentChange = (recentAvg - olderAvg) / olderAvg;

  // Threshold: >5% change is significant
  if (percentChange > 0.05) return 'rising';
  if (percentChange < -0.05) return 'falling';
  return 'stable';
}

/**
 * Get price history for a specific port-cargo pair.
 * Returns array of { price, time } sorted by time (oldest first).
 */
export function getPriceHistory(portId, cargoId) {
  const key = `${portId}_${cargoId}`;
  return GameState.priceHistory.get(key) || [];
}
